/**
 * @fileoverview Provider Outreach API Module
 * This module provides API functions for managing provider-related outreach operations including:
 * - Fetching priority outreach data with provider-specific filtering and context
 * - Managing provider-member relationships and healthcare coordination
 * - Retrieving filter options for providers, members, pharmacies, and program data
 * - Provider and member overview functionality with detailed profile information
 * - Mock data support for development and testing scenarios
 * - Search and filtering capabilities across healthcare entities
 * - Provider-specific data handling including specialties and contact information
 */

import api from "./api";
import { capitalizeFirstLetter } from "../utils/capitalize";

const hasFilterPayload = (body) =>
  !!(
    body &&
    (body.memberIds?.length ||
      body.providerIds?.length ||
      body.pharmacyIds?.length ||
      body.programIds?.length)
  );
const isSearchTerm = (search) => !!search?.trim();
const buildQueryParams = (qp, searchActive, filtersActive) => {
  const base = {
    outreachType: qp.outreachType,
    ...(qp.memberBadge && { memberBadge: qp.memberBadge }),
  };
  const filtered = Object.fromEntries(
    Object.entries(qp).filter(
      ([key]) =>
        !((searchActive || filtersActive) && (key === "page" || key === "size"))
    )
  );
  const pagination =
    !searchActive && !filtersActive
      ? {
        page: Number.isInteger(qp.page) ? qp.page : 0,
        size: Number.isInteger(qp.size) ? qp.size : 30,
      }
      : {};
  return { ...base, ...filtered, ...pagination };
};
const buildRequestBody = (body, searchActive) => {
  if (searchActive) return {};
  return {
    memberIds: Array.isArray(body.memberIds) ? body.memberIds : [],
    providerIds: Array.isArray(body.providerIds) ? body.providerIds : [],
    pharmacyIds: Array.isArray(body.pharmacyIds) ? body.pharmacyIds : [],
    programIds: Array.isArray(body.programIds) ? body.programIds : [],
  };
};
const formatAddressParts = (addressObj) => {
  if (!addressObj) return "";
  return [
    addressObj.line1,
    addressObj.city,
    addressObj.state,
    addressObj.postalCode,
  ]
    .map((p) => (p || "").trim())
    .filter(Boolean)
    .join(", ");
};
const transformMemberProgram = (prog) => ({
  name: prog.program?.programName || "Unknown Program",
  status: prog.status,
  dueDate: prog.dueDate,
  recentActivity: prog.recentActivityDto?.outcome || "N/A",
  provider: prog.provider,
  pharmacy: prog.pharmacy,
});
const transformGroupedResponse = (groups, outreachType) =>
  groups.flatMap((group) =>
    (group.members || []).map((member) => {
      const memberProgram = member.memberPrograms?.[0] || {};
      const recentActivity = memberProgram.recentActivityDto || {};
      return {
        key: group.key,
        label: group.label,
        memberCount: group.memberCount,
        earliestDueDate: group.earliestDueDate,
        recentActivityAt: group.recentActivityAt,
        member: {
          id: member.member.id,
          name: member.member.fullName,
          fullName: capitalizeFirstLetter(member.member.fullName),
          dob: member.member.dob,
          age: member.member.age,
          phoneNumber: member.member.phoneNumber,
          address: formatAddressParts(member.member.address),
          language: member.member.language || "N/A",
          timeZone: member.member.timezone || "N/A",
        },
        programs: member.memberPrograms?.map(transformMemberProgram) || [
          { name: "No programs", status: "" },
        ],
        pharmacy: memberProgram.pharmacy || { name: "N/A" },
        recentActivity: {
          status: recentActivity.outcome || "",
          timestamp: recentActivity.timestamp,
          role: recentActivity.recentActivityRole,
        },
        manualTask: {
          id: member?.manualTaskPk || "",
          noOfAttempts: member?.attemptCount || 0,
          taskNote: member?.note || "",
          outreachReason: member?.outreachReason || "",
        },
        provider: memberProgram.provider || { name: "N/A" },
        status: memberProgram.status || "",
        dueDate: memberProgram.dueDate || "N/A",
        memberBadge: member.memberBadge || "",
        type: outreachType || "N/A",
      };
    })
  );
const transformLegacyGroupsResponse = (groups, outreachType) =>
  groups
    .map((item) => ({
      ...item,
      name: item.name ? capitalizeFirstLetter(item.name) : item.name,
    }))
    .map((item) => ({
      label: item.label,
      member: {
        memberId: item.member?.id?.toString() || "",
        name: `${capitalizeFirstLetter(item.member?.fullName || "")} `.trim(),
        language: item.member?.language || "English",
        timeZone: item.member?.timezone || "EST",
        gender: "",
        age: item.member?.dob
          ? new Date().getFullYear() - new Date(item.member.dob).getFullYear()
          : "",
        dob: item.member?.dob || "",
        knownAllergies: item.member?.allergy ? [item.member.allergy] : [],
        contact: {
          phone: item.member?.phoneNumber || "",
          email: item.member?.email || "",
        },
        address: `${item.member?.address || ""}, ${item.member?.city || ""}, ${item.member?.state || ""
          }`.trim(),
      },
      memberBadge: item.memberBadge || "",
      type: outreachType || "N/A",
      provider: {
        name: item.memberPrograms?.[0]?.provider?.name
          ? capitalizeFirstLetter(item.memberPrograms[0].provider.name)
          : "",
        contact: item.memberPrograms?.[0]?.provider?.contact || "",
      },
      pharmacy: {
        name: item.memberPrograms?.[0]?.pharmacy?.name
          ? capitalizeFirstLetter(item.memberPrograms[0].pharmacy.name)
          : "",
        contact: item.memberPrograms?.[0]?.pharmacy?.contact || "",
      },
      programs:
        item.memberPrograms?.map((p) => ({
          name: p.program?.programName || "",
          status: p.status || "",
        })) || [],
      recentActivity: {
        status: item.outcome || "",
        role: item.recentActivityRole || "",
        timestamp: item.recentActivityAt || "",
      },
      dueDate: item?.earliestDueDate || "",
    }));

export const fetchProviderOutreach = async (params = {}) => {
  try {
    const { body: requestBody = {}, ...queryParams } = params;
    const searchActive = isSearchTerm(queryParams.search);
    const filtersActive = hasFilterPayload(requestBody);
    const finalQueryParams = buildQueryParams(
      queryParams,
      searchActive,
      filtersActive
    );
    const finalRequestBody = buildRequestBody(requestBody, searchActive);

    const response = await api.post(`/outreach/grouped`, finalRequestBody, {
      params: finalQueryParams,
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    if (Array.isArray(response.data?.groups)) {
      const transformedData = transformGroupedResponse(
        response.data.groups,
        queryParams.outreachType
      );
      return {
        content: transformedData,
        totalElements: response.data.totalElements || transformedData.length,
      };
    }

    if (Array.isArray(response.data.groups)) {
      const mappedContent = transformLegacyGroupsResponse(
        response.data.groups,
        queryParams.outreachType
      );
      return {
        content: mappedContent,
        totalElements: response.data.totalElements || mappedContent.length,
      };
    }

    return { content: [], totalElements: 0 };
  } catch (e) {
    return `fetchProviderOutreach failed: ${e instanceof Error ? e.message : String(e)}`;
  }
};

export const searchMembers = async (searchTerm) => {
  try {
    const url = `/outreach/filter`;
    const params = {
      outreachType: "PRIORITY_OUTREACH",
      page: 0,
      size: 30,
      search: searchTerm,
      filterType: "MEMBER",
    };

    const response = await api.get(url, {
      params,
      headers: {
        "Content-Type": "application/json",

        "ngrok-skip-browser-warning": "true",
      },
    });

    if (!Array.isArray(response.data)) {
      return [];
    }

    let results = [];
    if (response.data.content.length > 0) {
      results = response.data.content
        .map((item) => {
          let name = "";
          if (item.member?.name) name = item.member.name;
          else if (item.name) name = item.name;
          else if (item.memberName) name = item.memberName;
          else return null;

          return capitalizeFirstLetter(name);
        })
        .filter(Boolean);
    }

    return results;
  } catch (error) {
    return `searchMembers failed: ${error instanceof Error ? error.message : String(error)}`;
  }
};

export const searchProviders = async (searchTerm) => {
  try {
    const url = `/outreach/filter`;
    const params = {
      outreachType: "PRIORITY_OUTREACH",
      page: 0,
      size: 30,
      search: searchTerm,
      filterType: "PROVIDER",
    };

    const response = await api.get(url, {
      params,
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    if (Array.isArray(response.data?.content)) {
      return response.data.content.map((item) => ({
        ...item,
        name: item.name ? capitalizeFirstLetter(item.name) : item.name,
      }));
    }

    if (!Array.isArray(response.data)) {
      return [];
    }

    let results = [];
    if (response.data.content.length > 0) {
      results = response.data.content
        .map((item) => {
          let name = "";
          if (item.provider?.name) name = item.provider.name;
          else if (item.name) name = item.name;
          else if (item.providerName) name = item.providerName;
          else return null;

          return capitalizeFirstLetter(name);
        })
        .filter(Boolean);

      results = [...new Set(results)];
    }

    return results;
  } catch (error) {
    console.error("Unexpected error", error);
    return [];
  }
};

export const searchPharmacies = async (searchTerm) => {
  try {
    const url = `/outreach/filter`;
    const params = {
      outreachType: "PRIORITY_OUTREACH",
      page: 0,
      size: 30,
      search: searchTerm,
      filterType: "PHARMACY",
    };

    const response = await api.get(url, {
      params,
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    if (!Array.isArray(response.data.content)) {
      return [];
    }

    let results = [];
    if (response.data.content.length > 0) {
      results = response.data.content
        .map((item) => {
          let name = "";
          if (item.pharmacy?.name) name = item.pharmacy.name;
          else if (item.name) name = item.name;
          else if (item.pharmacyName) name = item.pharmacyName;
          else return null;

          return capitalizeFirstLetter(name);
        })
        .filter(Boolean);

      results = [...new Set(results)];
    }

    return results;
  } catch (error) {
    return `searchPharmacies failed: ${error instanceof Error ? error.message : String(error)}`;
  }
};

export const searchPrograms = async (searchTerm) => {
  try {
    const url = `/outreach/filter`;
    const params = {
      outreachType: "PRIORITY_OUTREACH",
      page: 0,
      size: 30,
      search: searchTerm,
      filterType: "PROGRAM",
    };

    const response = await api.get(url, {
      params,
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    if (!Array.isArray(response.data.content)) {
      return [];
    }

    let results = [];
    if (response.data.content.length > 0) {
      results = response.data.content
        .flatMap((item) => {
          let name = "";
          if (Array.isArray(item.programs))
            return item.programs.map((p) => capitalizeFirstLetter(p));
          if (item.program?.name) name = item.program.name;
          else if (item.name) name = item.name;
          else if (item.programName) name = item.programName;
          else return [];

          return [capitalizeFirstLetter(name)];
        })
        .filter(Boolean);

      results = [...new Set(results)];
    }

    return results;
  } catch (error) {
    console.error("Unexpected error", error);
    return [];
  }
};

export const getFilterOptions = async () => {
  try {
    const response = await fetchProviderOutreach({ size: 30 });

    if (!Array.isArray(response)) {
      return {
        members: [],
        providers: [],
        pharmacies: [],
        programs: [],
      };
    }

    const members = [
      ...new Set(
        response
          .filter((item) => item.member?.name)
          .map((item) => item.member.name)
          .filter(Boolean)
      ),
    ];

    const providers = [
      ...new Set(
        response
          .filter((item) => item.provider?.name)
          .map((item) => item.provider.name)
          .filter(Boolean)
      ),
    ];

    const pharmacies = [
      ...new Set(
        response
          .filter((item) => item.pharmacy?.name)
          .map((item) => item.pharmacy.name)
          .filter(Boolean)
      ),
    ];

    const programs = [
      ...new Set(
        response.flatMap((item) => item.programs || []).filter(Boolean)
      ),
    ];

    return {
      members,
      providers,
      pharmacies,
      programs,
    };
  } catch (error) {
    console.error("Unexpected error", error);
    return {
      members: [],
      providers: [],
      pharmacies: [],
      programs: [],
    };
  }
};

export const updateMemberPersonalInfo = async (memberId, personalInfo) => {
  try {
    const response = await api.patch(
      `/outreach/members/${memberId}/personal-info`,
      {
        allergy: Array.isArray(personalInfo.allergies)
          ? personalInfo.allergies.join(", ")
          : personalInfo.allergies,
        language: personalInfo.language,
        timezone: personalInfo.timeZone,

        gender: personalInfo.gender,
        dob: personalInfo.dateOfBirth,
      }
    );
    return response.data;
  } catch (error) {
    return `updateMemberPersonalInfo failed: ${error instanceof Error ? error.message : String(error)}`;
  }
};

export const fetchProviderOverview = async (memberId) => {
  try {
    const response = await api.get(`/api/providers/${memberId}`, {
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    if (response.data) {
      const providerData = response.data;
      const location1 = providerData.location1 || {};
      const location2 = providerData.location2 || {};

      return {
        name: capitalizeFirstLetter(providerData.fullName || ""),
        memberId: providerData.id ? providerData.id.toString() : "",
        npi: providerData.npi || "N/A",
        dea: providerData.dea || "N/A",
        status: capitalizeFirstLetter(
          providerData.status?.toLowerCase() || "active"
        ),

        phone: location1.phoneNumber || "",
        email: location1.email || "",

        address: {
          street: location1.address1 || "",
          address2: location1.address2 || "",
          city: location1.city || "",
          state: location1.state || "",
          zip: location1.zipCode || "",
          fullAddress: [
            location1.address1,
            location1.address2,
            location1.city,
            location1.state,
            location1.zipCode,
          ]
            .filter(Boolean)
            .join(", "),
        },

        faxNumber: location1.faxNumber || "",

        location2: {
          phoneNumber: location2.phoneNumber || "",
          email: location2.email || "",
          faxNumber: location2.faxNumber || "",
          address: {
            street: location2.address1 || "",
            address2: location2.address2 || "",
            city: location2.city || "",
            state: location2.state || "",
            zip: location2.zipCode || "",
            fullAddress: [
              location2.address1,
              location2.address2,
              location2.city,
              location2.state,
              location2.zipCode,
            ]
              .filter(Boolean)
              .join(", "),
          },
        },

        language: "",
        timeZone: "",
        contact: {
          phone: location1.phoneNumber || "",
          email: location1.email || "",
          fax: location1.faxNumber || "",
          address: [
            location1.address1,
            location1.address2,
            location1.city,
            location1.state,
            location1.zipCode,
          ]
            .filter(Boolean)
            .join(", "),
        },
      };
    }
  } catch (e) {
    return `fetchProviderOverview failed: ${e instanceof Error ? e.message : String(e)}`;
  }
};
export const fetchPharmacyOverview = async (memberId) => {
  try {
    const response = await api.get(`/api/pharmacies/${memberId}`, {
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    });

    if (response.data) {
      const providerData = response.data;
      const location1 = providerData.location1 || {};
      const location2 = providerData.location2 || {};

      return {
        name: capitalizeFirstLetter(providerData.name || ""),
        memberId: providerData.id ? providerData.id.toString() : "",
        nabp: providerData.nabp || "",
        dea: providerData.dea || "N/A",
        status: capitalizeFirstLetter(
          providerData.status?.toLowerCase() || "active"
        ),

        phone: location1.phoneNumber || "",
        email: location1.email || "",

        address: {
          street: location1.address1 || "",
          address2: location1.address2 || "",
          city: location1.city || "",
          state: location1.state || "",
          zip: location1.zipCode || "",
          fullAddress: [
            location1.address1,
            location1.address2,
            location1.city,
            location1.state,
            location1.zipCode,
          ]
            .filter(Boolean)
            .join(", "),
        },

        faxNumber: location1.faxNumber || "",

        location2: {
          phoneNumber: location2.phoneNumber || "",
          email: location2.email || "",
          faxNumber: location2.faxNumber || "",
          address: {
            street: location2.address1 || "",
            address2: location2.address2 || "",
            city: location2.city || "",
            state: location2.state || "",
            zip: location2.zipCode || "",
            fullAddress: [
              location2.address1,
              location2.address2,
              location2.city,
              location2.state,
              location2.zipCode,
            ]
              .filter(Boolean)
              .join(", "),
          },
        },

        language: "",
        timeZone: "",
        contact: {
          phone: location1.phoneNumber || "",
          email: location1.email || "",
          fax: location1.faxNumber || "",
          address: [
            location1.address1,
            location1.address2,
            location1.city,
            location1.state,
            location1.zipCode,
          ]
            .filter(Boolean)
            .join(", "),
        },
      };
    }
  } catch (e) {
    return `fetchPharmacyOverview failed: ${e instanceof Error ? e.message : String(e)}`;
  }
};
export const fetchPharmacyOpportunities = async (
  pharmacyId,
  searchQuery = ""
) => {
  try {
    const response = await api.get(
      `/outreach/pharmacies/${pharmacyId}/opportunities`,
      {
        params: searchQuery ? { search: searchQuery } : {},
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      }
    );
    return response.data;
  } catch (e) {
    return `fetchPharmacyOpportunities failed: ${e instanceof Error ? e.message : String(e)}`;
  }
};
export const fetchProviderOpportunities = async (
  providerId,
  searchQuery = ""
) => {
  try {
    const response = await api.get(
      `/outreach/providers/${providerId}/opportunities`,
      {
        params: searchQuery ? { search: searchQuery } : {},
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      }
    );
    return response.data;
  } catch (e) {
    return `fetchProviderOpportunities failed: ${e instanceof Error ? e.message : String(e)}`;
  }
};

export const updatePharmacyContactInfo = async (pharmacyId, contactInfo) => {
  try {
    const response = await api.patch(
      `/api/pharmacies/${pharmacyId}/contact`,
      contactInfo,
      {
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      }
    );

    return response.data;
  } catch (error) {
    return `updatePharmacyContactInfo failed: ${error instanceof Error ? error.message : String(error)}`;
  }
};

/**
 * Updates contact information for a provider
 * @param {string} providerId - The ID of the provider to update
 * @param {Object} contactInfo - The updated contact information
 * @param {Object} contactInfo.location1 - Primary location contact info
 * @param {string} contactInfo.location1.phoneNumber - Primary phone number
 * @param {string} contactInfo.location1.email - Primary email
 * @param {string} contactInfo.location1.faxNumber - Primary fax number
 * @param {string} contactInfo.location1.address1 - Primary address line 1
 * @param {string} contactInfo.location1.address2 - Primary address line 2 (optional)
 * @param {string} contactInfo.location1.city - Primary city
 * @param {string} contactInfo.location1.state - Primary state
 * @param {string} contactInfo.location1.zipCode - Primary zip code
 * @param {Object} contactInfo.location2 - Secondary location contact info (optional)
 * @param {string} contactInfo.location2.phoneNumber - Secondary phone number
 * @param {string} contactInfo.location2.email - Secondary email
 * @param {string} contactInfo.location2.faxNumber - Secondary fax number
 * @param {string} contactInfo.location2.address1 - Secondary address line 1
 * @param {string} contactInfo.location2.address2 - Secondary address line 2 (optional)
 * @param {string} contactInfo.location2.city - Secondary city
 * @param {string} contactInfo.location2.state - Secondary state
 * @param {string} contactInfo.location2.zipCode - Secondary zip code
 * @returns {Promise<Object>} The updated provider contact information
 * @throws {Error} If the API request fails
 */
export const updateProviderContactInfo = async (providerId, contactInfo) => {
  try {
    const response = await api.patch(
      `/api/providers/${providerId}/contact`,
      contactInfo,
      {
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      }
    );

    return response.data;
  } catch (error) {
    return `updateProviderContactInfo failed: ${error instanceof Error ? error.message : String(error)}`;
  }
};
